import { useState, type ChangeEvent } from 'react';
import { MenuItem, Select, TextField, type SelectChangeEvent } from '@mui/material';

import { IntervalValues, type NodeItem, type RetryInterval } from '@models/flows';

import { useToast } from '@hooks/useToast';

import recurringS from '../RecurringActionNodeConfiguration/RecurringActionNodeConfiguration.module.css';

type SuccessNodeConfigProps = {
  currentNode: NodeItem;
  onChange: (data: NodeItem) => void;
};

const RECHARGE_INTERVAL_OPTIONS = [
  { label: 'Days', value: IntervalValues.days },
  { label: 'Hours', value: IntervalValues.hours },
  { label: 'Minutes', value: IntervalValues.minutes },
];

const getSelectedIntervalValue = (currentNode: NodeItem) => {
  let intervalValue = IntervalValues.days;

  if (currentNode.retryInterval?.hours && currentNode.retryInterval.hours > 0) {
    intervalValue = IntervalValues.hours;
  }
  if (currentNode.retryInterval?.minutes && currentNode.retryInterval.minutes > 0) {
    intervalValue = IntervalValues.minutes;
  }

  return intervalValue;
};

function SuccessNodeConfiguration({ currentNode, onChange }: SuccessNodeConfigProps) {
  const [selectedRechargeInterval, setSelectedRechargeInterval] = useState(
    getSelectedIntervalValue(currentNode),
  );
  const toast = useToast();

  if (!currentNode) return null;

  const retryInterval: RetryInterval = currentNode.retryInterval || {
    days: 0,
    hours: 0,
    minutes: 0,
  };

  const handleRechargeIntervalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Number((event.target as HTMLInputElement).value);

    if (Number.isNaN(value)) return;
    if (value < 0) {
      toast.error('Recharge interval cannot be negative');
      return;
    }

    const updated: NodeItem = {
      ...currentNode,
      retryInterval: {
        days: selectedRechargeInterval === IntervalValues.days ? value : 0,
        hours: selectedRechargeInterval === IntervalValues.hours ? value : 0,
        minutes: selectedRechargeInterval === IntervalValues.minutes ? value : 0,
      },
    };
    delete updated.maxRetries;
    onChange(updated);
  };

  const handleIntervalTypeChange = (event: SelectChangeEvent) => {
    setSelectedRechargeInterval((event.target as HTMLInputElement).value);
  };

  return (
    <div className={recurringS.recurringConfigWrapper}>
      <div className={recurringS.actionBlock}>
        <p className={recurringS.actionTitle}>Recharge every:</p>
        <div className={recurringS.retryIntervalContainer}>
          <TextField
            size="small"
            type="number"
            value={
              retryInterval[selectedRechargeInterval as keyof RetryInterval] === 0
                ? ''
                : retryInterval[selectedRechargeInterval as keyof RetryInterval]
            }
            className={recurringS.flowEditActionBarNameInput}
            onChange={handleRechargeIntervalChange}
            required
          />
          <Select size="small" value={selectedRechargeInterval} onChange={handleIntervalTypeChange}>
            {RECHARGE_INTERVAL_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
    </div>
  );
}

export default SuccessNodeConfiguration;
